/**
 * Portfolio API Hooks
 *
 * React Query hooks for portfolio-related API operations.
 */

import {
  type EnrichedHolding,
  type HoldingsAccountSummary as AccountSummary,
  type HoldingsSummaryResponse,
} from '@luminaria/shared';
import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { invalidate } from '../api/invalidation';
import {
  portfoliosApi,
  type AccountView,
  type Portfolio,
} from '../api/portfoliosApi';
import { AUTO_REFRESH_INTERVAL_MS } from '../api/queryClient';
import { portfolioKeys } from '../api/queryKeys';

export type { AccountView, Portfolio };

/**
 * Holdings Summary types (from backend) — re-exported under this package's
 * established names from the shared contract, which is the single source
 * of truth for portfolioHoldingsService.ts's response shape.
 */
export type { EnrichedHolding, AccountSummary, HoldingsSummaryResponse };

/**
 * Fetch all portfolios for the current user
 */
export function usePortfolios(options?: Omit<UseQueryOptions<Portfolio[]>, 'queryKey' | 'queryFn'>) {
  return useQuery<Portfolio[]>({
    queryKey: portfolioKeys.list(),
    queryFn: () => portfoliosApi.list(),
    ...options,
  });
}

/**
 * Delete ALL portfolios for the current user (dangerous!)
 */
export function useDeleteAllPortfolios() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => portfoliosApi.removeAll(),
    onSuccess: () => {
      // Drop every cached portfolio view, then refetch whatever is mounted
      queryClient.removeQueries({ queryKey: portfolioKeys.all });
      void invalidate(queryClient, ['portfolios', 'accounts']);
    },
  });
}

/**
 * Fetch comprehensive portfolio holdings summary with live quotes.
 * This is the unified endpoint that replaces separate holdings + quotes fetching.
 *
 * Returns:
 * - Holdings enriched with current prices, market values, and gain/loss
 * - Live quotes (15-min cache)
 * - Portfolio summary (total value, cost basis, gain/loss)
 * - Per-account breakdown
 * - Quote freshness timestamp
 */
export function useHoldingsSummary(
  portfolioId: string,
  accountIds?: string[],
  options?: Omit<UseQueryOptions<HoldingsSummaryResponse>, 'queryKey' | 'queryFn'>
) {
  return useQuery<HoldingsSummaryResponse>({
    queryKey: portfolioKeys.holdingsSummary({ portfolioId, accountIds }),
    queryFn: () => portfoliosApi.holdingsSummary(portfolioId, accountIds),
    enabled: !!portfolioId,
    staleTime: 30 * 1000, // Let backend quote cache own most caching behavior
    refetchOnMount: 'always',
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    refetchInterval: AUTO_REFRESH_INTERVAL_MS,
    ...options,
  });
}

/**
 * Fetch accounts with pre-joined holdings and a transaction count.
 * This is optimized to avoid N+1 queries on the client side: the backend
 * joins the data and returns enriched account views.
 */
export type AccountsViewKey = ReturnType<typeof portfolioKeys.accountsView>;

export function useAccountsView(
  portfolioId: string,
  options?: Omit<UseQueryOptions<AccountView[], Error, AccountView[], AccountsViewKey>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    ...accountsViewQueryOptions(portfolioId),
    ...options,
  });
}

/** The client's one accounts query (WEBD-24); `useAccounts` selects from it. */
export function accountsViewQueryOptions(portfolioId: string) {
  return {
    queryKey: portfolioKeys.accountsView(portfolioId),
    queryFn: () => portfoliosApi.accountsView(portfolioId),
    enabled: !!portfolioId,
  };
}
